import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, RefreshCw, X } from "lucide-react";
import { Button } from "../shared/Button";

/**
 * LA CÁMARA DE VERDAD.
 *
 * Con `getUserMedia` y no con `<input capture>`: esa etiqueta la ignoran los
 * navegadores de escritorio, así que el botón «tomar foto» abría el selector
 * de archivos y parecía roto. Es un fallo que ya hubo que corregir en el
 * sistema de clínicas, y se corrige aquí de entrada.
 *
 * Va en un portal, encima de todo. Y el botón de cambiar de cámara para el
 * evento, porque si no, el clic sube hasta la ventana que hay debajo y la
 * cierra — otro fallo que ya se pagó una vez.
 */
export default function CamaraFoto({ alCapturar, alCerrar }) {
  const video = useRef(null);
  const flujo = useRef(null);
  const [frontal, setFrontal] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelado = false;

    const abrir = async () => {
      try {
        if (flujo.current) flujo.current.getTracks().forEach((t) => t.stop());
        const nuevo = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: frontal ? "user" : "environment" },
          audio: false,
        });
        if (cancelado) {
          nuevo.getTracks().forEach((t) => t.stop());
          return;
        }
        flujo.current = nuevo;
        if (video.current) video.current.srcObject = nuevo;
      } catch {
        setError("No se pudo abrir la cámara. Revise los permisos del navegador.");
      }
    };

    abrir();
    return () => {
      cancelado = true;
      if (flujo.current) flujo.current.getTracks().forEach((t) => t.stop());
    };
  }, [frontal]);

  const capturar = () => {
    const v = video.current;
    if (!v) return;
    const lienzo = document.createElement("canvas");
    lienzo.width = v.videoWidth;
    lienzo.height = v.videoHeight;
    lienzo.getContext("2d").drawImage(v, 0, 0);
    lienzo.toBlob(
      (blob) => {
        if (blob) alCapturar(new File([blob], `foto-${Date.now()}.jpg`, { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92
    );
  };

  return createPortal(
    <div
      // Marca que las ventanas de debajo respetan para no cerrarse solas.
      data-dialogo-encima="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#000",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", padding: "var(--space-4)" }}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); alCerrar(); }}
          style={{ background: "transparent", border: 0, color: "#fff", cursor: "pointer" }}
          aria-label="Cerrar"
        >
          <X size={28} />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setFrontal((f) => !f); }}
          style={{ background: "transparent", border: 0, color: "#fff", cursor: "pointer" }}
          aria-label="Cambiar de cámara"
        >
          <RefreshCw size={24} />
        </button>
      </div>

      {error ? (
        <p style={{ color: "#fff", textAlign: "center", padding: "var(--space-6)" }}>{error}</p>
      ) : (
        <video
          ref={video}
          autoPlay
          playsInline
          muted
          style={{ flex: 1, width: "100%", objectFit: "cover" }}
        />
      )}

      <div style={{ display: "grid", placeItems: "center", padding: "var(--space-6)" }}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); capturar(); }}
          disabled={Boolean(error)}
          style={{
            width: "72px",
            height: "72px",
            borderRadius: "50%",
            border: "4px solid #fff",
            background: "var(--gold)",
            display: "grid",
            placeItems: "center",
            cursor: "pointer",
          }}
          aria-label="Tomar la foto"
        >
          <Camera size={28} color="#15171b" />
        </button>
      </div>
    </div>,
    document.body
  );
}
